import React, { useCallback } from 'react'
import { Stack, Typography } from '@mui/material'
import type { OrgMemberRow } from 'pure-js/types/OrganizationMembership'

import { Button, Spinner } from '../../../../interaxDesignSystemComponents'
import { DefaultModal } from '../../../../shared/components/DefaultModal'
import { TESTIDS } from '../../../../config/testids'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { LeaderScopeEditor } from './LeaderScopeEditor'
import { getOrgMemberLabel } from './leaderScopeHelpers'
import {
  LEADER_SCOPE_MODAL_MAX_WIDTH,
  leaderScopeErrorTextSx,
  leaderScopeModalContentSx,
  leaderScopeTitleSx
} from './leaderScopeStyles'
import { useLeaderScopeSelection } from './useLeaderScopeSelection'

export type LeaderScopeModalProps = {
  target: OrgMemberRow | null
  organizationId: string
  members: OrgMemberRow[]
  onClose: () => void
}

/** Where the people one leader leads are picked. */
export const LeaderScopeModal: React.FC<LeaderScopeModalProps> = ({ target, organizationId, members, onClose }) => {
  const { t } = useTranslation()
  const isOpen = target !== null
  const selection = useLeaderScopeSelection({ isOpen, organizationId, leader: target, members })
  const { save } = selection

  const saveAndClose = useCallback(async () => {
    const hasSaved = await save()
    if (hasSaved) onClose()
  }, [save, onClose])

  const handleSaveClick = useCallback(() => void saveAndClose(), [saveAndClose])

  const hasLoadFailed = !selection.isLoading && selection.hasLoadFailed
  const isEditorShown = !selection.isLoading && !selection.hasLoadFailed
  const isSaveDisabled = !selection.isSaveAllowed || selection.isSaving

  return (
    <DefaultModal isOpen={isOpen} onClose={onClose} maxWidth={LEADER_SCOPE_MODAL_MAX_WIDTH}>
      <Stack spacing={2} sx={leaderScopeModalContentSx} data-testid={TESTIDS.LEADER_SCOPE_MODAL}>
        <Typography sx={leaderScopeTitleSx}>
          {t('org.leaderScope.title', { name: getOrgMemberLabel(target) })}
        </Typography>

        {selection.isLoading && <Spinner color="sun" />}

        {hasLoadFailed && <Typography sx={leaderScopeErrorTextSx}>{t('org.leaderScope.loadError')}</Typography>}

        {isEditorShown && <LeaderScopeEditor selection={selection} />}

        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button variant="secondary" size="small" onClick={onClose}>
            {t('org.members.invite.cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleSaveClick}
            disabled={isSaveDisabled}
            data-testid={TESTIDS.LEADER_SCOPE_SAVE_BTN}
          >
            {t('common.save')}
          </Button>
        </Stack>
      </Stack>
    </DefaultModal>
  )
}
